import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle, MapPin, TrendingUp, Shield, Star, DollarSign,
  ChevronDown, ChevronUp, ArrowRight, Users, Zap, Lock, BarChart2
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { INDUSTRY_TYPES } from "@/constants/industries";
import SEOHead from "@/components/SEOHead";

const steps = [
  {
    num: "01",
    title: "Pick Your Industry & ZIP",
    desc: "Choose from 12 industries. Enter any US ZIP code. We instantly verify if it's available — first come, first served.",
    color: "#2ECC71",
  },
  {
    num: "02",
    title: "Create Your Account & Pay $150",
    desc: "Register in 60 seconds. One-time payment of $150 via Stripe. Your listing goes live the moment payment clears.",
    color: "#003366",
  },
  {
    num: "03",
    title: "Own Your Territory All Season",
    desc: "Your banner rotates on every search in your ZIP. Leads contact you directly. Clicks tracked in your portal. No revenue share, ever.",
    color: "#E74C3C",
  },
];

const faqs = [
  { q: "What does 'exclusive' mean?", a: "One advertiser per industry per ZIP. If you're the only Insurance Agent in ZIP 33101, every person searching for an insurance agent in that ZIP sees only you — no competitors, no bidding war." },
  { q: "Which industries are available?", a: "Tax Professionals, Legal Help / Tax Lawyers, Debt Relief, Insurance Agents, Auto Dealers, Retirement Planners, Mortgage Brokers, Personal Loan providers, Real Estate Agents, Financial Advisors, Home Improvement contractors, and Job Postings. One exclusive spot per industry per ZIP." },
  { q: "How long does my listing last?", a: "Through April 15, 2027 — the end of the 2027 tax season. We'll send renewal reminders at 60, 30, and 7 days before expiration so you never lose your spot." },
  { q: "Can I claim more than one ZIP code?", a: "Yes — claim as many as you want at $150 each. Many advertisers claim entire metro areas. The more ZIPs you hold, the larger your exclusive territory." },
  { q: "What do I get for $150?", a: "Exclusive listing shown to all searchers in your ZIP, a rotating banner ad on the Territory Map, a Verified Provider badge, click tracking and lead analytics, and direct lead routing — no middlemen." },
  { q: "What if someone already claimed my ZIP?", a: "You'll see it's taken when you search. But you can claim the same ZIP for a different industry — or any neighboring ZIP code that's still open." },
  { q: "Is there a refund policy?", a: "Due to the exclusivity model, payments are non-refundable once your listing is live. That's the deal that protects your exclusivity from competitors too." },
];

const stats = [
  { value: "42,000+", label: "US ZIP Codes Available" },
  { value: `${INDUSTRY_TYPES.filter(t => t.value !== "all").length}`, label: "Industries to Claim" },
  { value: "$150", label: "One-Time Season Price" },
  { value: "100%", label: "Exclusive Per ZIP/Industry" },
];

export default function Advertise() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Advertise to Local Taxpayers — Claim a ZIP Code Territory"
        description="Claim an exclusive ZIP code on NextDayRefund.com and become the only verified provider in your area. $150/year per ZIP. 12 industries. 50 states. 2027 tax season."
        canonical="/advertise"
        jsonLd={{
          "@type": "Service",
          "name": "ZIP Code Advertising — NextDayRefund.com",
          "provider": { "@type": "Organization", "name": "Affordable Advertising Solutions LLC" },
          "description": "Exclusive local ZIP code advertising for tax professionals, debt relief, insurance, and 9 other industries.",
          "offers": { "@type": "Offer", "price": "150.00", "priceCurrency": "USD", "description": "Exclusive ZIP code territory for 1 year (through April 15, 2027)" }
        }}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Advertise", url: "/advertise" },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#003366] py-16" data-testid="advertise-hero">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#2ECC71]/15 text-[#2ECC71] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 border border-[#2ECC71]/30">
              <Zap size={12} /> {INDUSTRY_TYPES.filter(t => t.value !== "all").length} Industries · Exclusive Per ZIP · $150/Year
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-5">
              Own Your Market.<br />
              <span className="text-[#2ECC71]">Zero Competition.</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
              Claim an exclusive ZIP code for your industry. Every person searching NextDayRefund.com in your territory sees <strong className="text-white">only you</strong> — not your competitors. One advertiser per industry per ZIP. $150 for the entire 2027 season.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/claim" className="btn-action !px-8 !py-3.5 !text-base flex items-center gap-2 justify-center" data-testid="advertise-hero-cta">
                Check Your ZIP — It's Free <ArrowRight size={18} />
              </Link>
              <Link to="/map" className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-3.5 rounded-xl font-semibold text-base transition-all flex items-center gap-2 justify-center">
                <MapPin size={16} /> View Territory Map
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0F172A] py-6">
        <div className="section-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map(s => (
              <div key={s.label}>
                <div className="text-2xl sm:text-3xl font-black text-[#2ECC71]">{s.value}</div>
                <div className="text-slate-400 text-sm mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="section-container">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-widest text-[#003366] mb-2">{INDUSTRY_TYPES.filter(t => t.value !== "all").length} Industries Available</div>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">Find Your Industry. Claim Your ZIP.</h2>
            <p className="text-[#64748B] mt-2 max-w-xl mx-auto">Every industry gets its own exclusive slot per ZIP code. Click yours to claim now.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {INDUSTRY_TYPES.filter(t => t.value !== "all").map(ind => {
              const IconComp = ind.icon;
              return (
                <Link
                  key={ind.value}
                  to={`/claim?type=${ind.value}`}
                  data-testid={`industry-card-${ind.value}`}
                  className="group bg-white border border-slate-200 rounded-xl p-5 hover:border-[#003366]/40 hover:shadow-md transition-all text-center"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${ind.color}18` }}>
                    <IconComp size={22} style={{ color: ind.color }} />
                  </div>
                  <div className="font-bold text-[#0F172A] text-sm">{ind.label}</div>
                  <div className="text-xs text-[#64748B] mt-1 leading-tight">{ind.desc}</div>
                  <div className="mt-3 text-xs font-bold text-[#003366] flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Claim ZIP <ArrowRight size={11} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="section-container">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-widest text-[#003366] mb-2">Simple 3-Step Process</div>
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">Live in Under 5 Minutes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={i} className="text-center" data-testid={`step-${i + 1}`}>
                <div className="text-5xl font-black mb-4" style={{ color: step.color }}>{step.num}</div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{step.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/claim" className="btn-action !px-10 !py-4 !text-base inline-flex items-center gap-2" data-testid="how-it-works-cta">
              Check My ZIP Now — Free <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 bg-[#003366]">
        <div className="section-container">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black text-white">Everything Included for $150</h2>
            <p className="text-slate-300 mt-2">One payment. No monthly fees. No revenue share. No surprises.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { icon: Lock, title: "Exclusive Territory Lock", desc: "Zero competitors in your ZIP for your industry. Guaranteed by the platform." },
              { icon: Users, title: "Direct Lead Routing", desc: "Searchers in your ZIP see only your listing and contact you directly." },
              { icon: Star, title: "Rotating Banner Ad", desc: "Your banner displays on the Territory Map page and rotates in front of every visitor." },
              { icon: BarChart2, title: "Click & Lead Analytics", desc: "See exactly how many clicks and leads your listing generates in your Partner Portal." },
              { icon: Shield, title: "Verified Provider Badge", desc: "Display your badge on your website and social profiles to build instant trust." },
              { icon: TrendingUp, title: "Renewal Protection", desc: "We'll remind you before your listing expires so you never lose your territory to a competitor." },
            ].map((item, i) => {
              const Ic = item.icon;
              return (
                <div key={i} className="bg-white/10 border border-white/20 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#2ECC71]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Ic size={18} className="text-[#2ECC71]" />
                  </div>
                  <div>
                    <div className="font-bold text-white text-sm">{item.title}</div>
                    <div className="text-slate-300 text-xs mt-1 leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="section-container max-w-2xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-[#0F172A]">Common Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-[#0F172A] text-sm pr-4">{faq.q}</span>
                  {openFaq === i ? <ChevronUp size={16} className="text-[#003366] flex-shrink-0" /> : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-[#64748B] text-sm leading-relaxed border-t border-slate-100 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="section-container text-center max-w-2xl">
          <div className="text-[#2ECC71] text-xs font-bold uppercase tracking-widest mb-3">Limited Spots Per ZIP</div>
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
            Your ZIP Is Either Available<br />or It Isn't. Check Now.
          </h2>
          <p className="text-slate-400 mb-8">Free to check. Takes 10 seconds. If it's available, claim it before someone else does.</p>
          <Link to="/claim" className="btn-action !px-12 !py-4 !text-lg inline-flex items-center gap-3" data-testid="final-cta">
            Check My ZIP — Free <ArrowRight size={20} />
          </Link>
          <div className="mt-5 text-slate-500 text-sm">
            Already have an account? <Link to="/login" className="text-[#2ECC71] hover:underline font-semibold">Sign in →</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
